import React, { useState, useEffect, useCallback } from "react";
import { FaSearch, FaFilter, FaDownload, FaPlus, FaTimes, FaFileInvoice } from "react-icons/fa";
import "../../../styles/dashboard/SuperAdmin/TransactionHistory.css";
import { addSuperadminDeduction, getAdminTransactions, getSuperadminDeductions, getSuperadminTransactions } from "../../services/api";

const API_URL = process.env.REACT_APP_API_URL;
const STICKY_COLUMN_STYLES = {
  srNo: { left: 0, minWidth: 84 },
  billDate: { left: 84, minWidth: 180 },
  invoiceNo: { left: 264, minWidth: 190 },
  referenceNo: { left: 454, minWidth: 190 },
};
const INVOICE_COLUMNS = [
  { key: "srNo", label: "Sr.No" },
  { key: "billDate", label: "BILL DATE" },
  { key: "invoiceNo", label: "Invoice No" },
  { key: "referenceNo", label: "Reference No." },
  { key: "customerName", label: "Customer Name" },
  { key: "customerGstin", label: "Customer GSTIN" },
  { key: "customerPanNumber", label: "Customer PAN Number" },
  { key: "countryOfSupply", label: "Country of Supply" },
  { key: "stateOfSupply", label: "STATE OF SUPPLY" },
  { key: "hsnCode", label: "HSN CODE" },
  { key: "billingAddress", label: "Billing Address" },
  { key: "itemDescription", label: "Item Discription" },
  { key: "taxableAmount", label: "Taxable Amount" },
  { key: "platformFee", label: "Platform Fee (2%)" },
  { key: "cgstAmount", label: "CGST Amount" },
  { key: "sgstAmount", label: "SGST Amount" },
  { key: "igstAmount", label: "IGST Amount" },
  { key: "totalAmount", label: "Total Amount" },
  { key: "companyName", label: "Company Name" },
  { key: "gstinNumber", label: "GSTIN Number" },
  { key: "panNumber", label: "PAN Number" },
  { key: "companyAddress", label: "Company Address" },
  { key: "updatedBy", label: "Updated By" },
  { key: "paymentMode", label: "Payment Mode" },
  { key: "utrNumber", label: "UTR Number" },
  { key: "attachmentUrl", label: "Attachment" },
];

function getToken() {
  return sessionStorage.getItem("token") || localStorage.getItem("token");
}

export default function TransactionHistory({ role = "superadmin" }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [deductionSubmitting, setDeductionSubmitting] = useState(false);
  const [deductionForm, setDeductionForm] = useState({
    purpose: "",
    amount: "",
    updatedBy: "",
    date: new Date().toISOString().split("T")[0],
    utrNumber: "",
    paymentMode: "",
    attachmentUrl: "",
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, totalRecords: 0, totalPages: 0 });

  const fetchTransactions = useCallback(
    async (page = 1) => {
      setLoading(true);
      const dateFrom = filterDateFrom || (filterMonth || filterYear ? buildDateFrom(filterMonth, filterYear) : "");
      const dateTo = filterDateTo || filterDateFrom || (filterMonth || filterYear ? buildDateTo(filterMonth, filterYear) : "");
      const params = {
        page,
        limit: pagination.limit,
        type: filterStatus === "all" ? "" : filterStatus,
        search: searchTerm,
        dateFrom,
        dateTo,
      };

      const result =
        role === "superadmin"
          ? await getSuperadminTransactions(params)
          : await getAdminTransactions(params);

      if (result && result.success) {
        const raw = Array.isArray(result.data) ? result.data : result.data && result.data.data ? result.data.data : [];
        setTransactions(raw);
        // pagination can live in result.raw (top-level) or result.data (nested)
        const paginationData =
          result.raw?.pagination ||
          (result.data && !Array.isArray(result.data) && result.data.pagination) ||
          null;
        if (paginationData) {
          setPagination((p) => ({ ...p, ...paginationData, page }));
        } else {
          // fallback: compute from array length
          setPagination((p) => ({
            ...p,
            page,
            totalRecords: raw.length,
            totalPages: Math.ceil(raw.length / p.limit) || 1,
          }));
        }
      }
      setLoading(false);
    },
    [role, filterStatus, searchTerm, filterDateFrom, filterDateTo, filterMonth, filterYear, pagination.limit]
  );

  useEffect(() => {
    fetchTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, filterStatus, filterDateFrom, filterDateTo, filterMonth, filterYear]);

  useEffect(() => {
    const t = setTimeout(() => fetchTransactions(1), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchDeductions = useCallback(async () => {
    if (role !== "superadmin") {
      setDeductions([]);
      return;
    }

    const result = await getSuperadminDeductions({
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
    });

    if (result?.success) {
      setDeductions(Array.isArray(result.data) ? result.data : []);
    }
  }, [role, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchDeductions();
  }, [fetchDeductions]);

  const handleViewInvoice = async (txnId) => {
    const base = role === "superadmin" ? API_URL + "/superadmin" : API_URL + "/admin";
    const url = base + "/transactions/" + txnId + "/invoice";
    try {
      const res = await fetch(url, { headers: { Authorization: "Bearer " + getToken() } });
      if (!res.ok) {
        alert("Invoice not available for this transaction.");
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (err) {
      alert("Failed to load invoice.");
    }
  };

  const filteredDeductions = deductions.filter((ded) => {
    if (filterDateFrom || filterDateTo) {
      const d = ded.date;
      if (filterDateFrom && d < filterDateFrom) return false;
      if (filterDateTo && d > filterDateTo) return false;
      return true;
    }
    if (!filterMonth && !filterYear) return true;
    const d = new Date(ded.date);
    if (filterMonth && d.getMonth() + 1 !== Number(filterMonth)) return false;
    if (filterYear && d.getFullYear() !== Number(filterYear)) return false;
    return true;
  });

  const normalizeStatus = (s) => {
    const l = (s || "").toLowerCase();
    if (l === "completed") return "Success";
    if (l === "pending") return "Pending";
    if (l === "failed") return "Failed";
    return s || "Unknown";
  };

  const formatAmount = (value) => Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const buildInvoiceRow = (txn, index) => ({
    srNo: (pagination.page - 1) * pagination.limit + index + 1,
    billDate: txn.billDate || (txn.createdAt ? new Date(txn.createdAt).toLocaleString("en-IN") : "-"),
    invoiceNo: txn.invoiceNo || txn.invoiceNumber || "-",
    referenceNo: txn.referenceNo || txn.referenceId || "-",
    paymentMode: txn.paymentMode || txn.metadata?.paymentMode || "-",
    utrNumber: txn.utrNumber || txn.metadata?.utrNumber || "-",
    attachmentUrl: txn.attachmentUrl || txn.metadata?.attachmentUrl || "-",
    updatedBy: "-",
    customerName: txn.customerName || txn.organizationName || "-",
    customerGstin: txn.customerGstin || "-",
    customerPanNumber: txn.customerPanNumber || "-",
    countryOfSupply: txn.countryOfSupply || "India",
    stateOfSupply: txn.stateOfSupply || "-",
    hsnCode: txn.hsnCode || "-",
    billingAddress: txn.billingAddress || "-",
    itemDescription: txn.itemDescription || txn.description || "-",
    taxableAmount: txn.taxableAmount || txn.amount || 0,
    platformFee: txn.platformFee || 0,
    cgstAmount: txn.cgstAmount || 0,
    sgstAmount: txn.sgstAmount || 0,
    igstAmount: txn.igstAmount || 0,
    totalAmount: txn.totalAmount || 0,
    companyName: txn.companyName || "-",
    gstinNumber: txn.gstinNumber || "-",
    panNumber: txn.panNumber || "-",
    companyAddress: txn.companyAddress || "-",
    rawTxnId: txn.id,
    status: normalizeStatus(txn.status),
    type: txn.type || "credit",
    sortTimestamp: txn.createdAt ? new Date(txn.createdAt).getTime() : 0,
  });

  const transactionRows = transactions.map((txn, index) => buildInvoiceRow(txn, index));
  const deductionRows = filteredDeductions.map((ded) => {
    const createdAt = ded.createdAt || (ded.date ? `${ded.date}T00:00:00` : null);
    const amount = Number(ded.amount || 0);
    return {
      srNo: 0,
      billDate: createdAt ? new Date(createdAt).toLocaleString("en-IN") : "-",
      invoiceNo: "MANUAL-DEDUCTION",
      referenceNo: `DED-${ded.id}`,
      paymentMode: ded.paymentMode || "-",
      utrNumber: ded.utrNumber || "-",
      attachmentUrl: ded.attachmentUrl || "-",
      updatedBy: ded.updatedBy || "Super Admin",
      customerName: "-",
      customerGstin: "-",
      customerPanNumber: "-",
      countryOfSupply: "India",
      stateOfSupply: "-",
      hsnCode: "-",
      billingAddress: "-",
      itemDescription: `Manual deduction - ${ded.purpose || "N/A"}`,
      taxableAmount: -amount,
      platformFee: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      totalAmount: -amount,
      companyName: "-",
      gstinNumber: "-",
      panNumber: "-",
      companyAddress: "-",
      rawTxnId: null,
      status: "Success",
      type: "debit",
      sortTimestamp: createdAt ? new Date(createdAt).getTime() : 0,
    };
  });

  const invoiceRows = [...transactionRows, ...deductionRows]
    .sort((a, b) => (b.sortTimestamp || 0) - (a.sortTimestamp || 0))
    .map((row, index) => ({
      ...row,
      srNo: (pagination.page - 1) * pagination.limit + index + 1,
    }));

  const getColumnClassName = (key) => {
    const classes = [];
    if (STICKY_COLUMN_STYLES[key]) classes.push("sticky-col");
    if (key === "invoiceNo" || key === "referenceNo" || key === "customerGstin" || key === "customerPanNumber" || key === "gstinNumber" || key === "panNumber") {
      classes.push("nowrap-cell");
    }
    if (["taxableAmount", "platformFee", "cgstAmount", "sgstAmount", "igstAmount", "totalAmount"].includes(key)) {
      classes.push("amount-cell");
    }
    if (key === "totalAmount") classes.push("total-amount-cell");
    return classes.join(" ");
  };

  const visibleColumns = INVOICE_COLUMNS;

  const getColumnStyle = (key) => {
    const stickyStyle = STICKY_COLUMN_STYLES[key];
    return stickyStyle ? { left: `${stickyStyle.left}px`, minWidth: `${stickyStyle.minWidth}px` } : undefined;
  };

  const totalAmount = transactions.reduce((s, t) => s + Number(t.totalAmount || t.amount || 0), 0);
  const totalDeductionsAmt = filteredDeductions.reduce((s, d) => s + Number(d.amount || 0), 0);
  const netAmount = totalAmount - totalDeductionsAmt;

  const handleAddDeduction = async (e) => {
    e.preventDefault();
    if (!deductionForm.purpose || !deductionForm.amount || !deductionForm.updatedBy) {
      return;
    }

    setDeductionSubmitting(true);
    const result = await addSuperadminDeduction({
      purpose: deductionForm.purpose,
      amount: deductionForm.amount,
      updatedBy: deductionForm.updatedBy,
      date: deductionForm.date,
      utrNumber: deductionForm.utrNumber,
      paymentMode: deductionForm.paymentMode,
      attachmentUrl: deductionForm.attachmentUrl,
    });

    if (result?.success) {
      await fetchDeductions();
      setDeductionForm({
        purpose: "",
        amount: "",
        updatedBy: "",
        date: new Date().toISOString().split("T")[0],
        utrNumber: "",
        paymentMode: "",
        attachmentUrl: "",
      });
      setShowDeductionModal(false);
    } else {
      alert(result?.error || "Failed to add deduction");
    }

    setDeductionSubmitting(false);
  };

  const handleExportCSV = () => {
    const headers = INVOICE_COLUMNS.map((column) => column.label);
    const rows = invoiceRows.map((row) => [
      row.srNo,
      row.billDate,
      row.invoiceNo,
      row.referenceNo,
      row.customerName,
      row.customerGstin,
      row.customerPanNumber,
      row.countryOfSupply,
      row.stateOfSupply,
      row.hsnCode,
      row.billingAddress,
      row.itemDescription,
      formatAmount(row.taxableAmount),
      formatAmount(row.platformFee),
      formatAmount(row.cgstAmount),
      formatAmount(row.sgstAmount),
      formatAmount(row.igstAmount),
      formatAmount(row.totalAmount),
      row.companyName,
      row.gstinNumber,
      row.panNumber,
      row.companyAddress,
      row.updatedBy,
      row.paymentMode,
      row.utrNumber,
      row.attachmentUrl,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((v) => '"' + v + '"').join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transactions_" + new Date().toISOString().split("T")[0] + ".csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadInvoices = async () => {
    setDownloading(true);
    try {
      const token = getToken();
      const rolePrefix = role === "superadmin" ? "/superadmin" : "/admin";
      
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("type", filterStatus);
      if (filterDateFrom || filterDateTo) {
        if (filterDateFrom) params.append("startDate", filterDateFrom);
        if (filterDateTo) params.append("endDate", filterDateTo);
      } else if (filterMonth || filterYear) {
        const startDate = `${filterYear || new Date().getFullYear()}-${String(filterMonth).padStart(2, "0")}-01`;
        params.append("startDate", startDate);
        const endDate = buildDateTo(filterMonth, filterYear);
        params.append("endDate", endDate);
      }

      const res = await fetch(`${API_URL}${rolePrefix}/transactions/download/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to download invoices");
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `transaction-invoices-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download invoices error:", err);
      alert("Failed to download invoices");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="transaction-history-container">
      <div className="transaction-header">
        <h2>Transaction History</h2>
        <p className="text-muted">Wallet recharge transactions across organizations</p>
      </div>

      <div className="transaction-controls">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by organization, description, or reference ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <FaFilter className="filter-icon" />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
            <option value="all">All Types</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>
        </div>

        <div className="filter-group date-range-group">
          <span className="date-range-label">From</span>
          <input
            type="date"
            value={filterDateFrom}
            max={filterDateTo || undefined}
            onChange={(e) => {
              setFilterDateFrom(e.target.value);
              if (e.target.value) { setFilterMonth(""); setFilterYear(""); }
            }}
            className="filter-select filter-date-input"
          />
          <span className="date-range-label">To</span>
          <input
            type="date"
            value={filterDateTo}
            min={filterDateFrom || undefined}
            onChange={(e) => {
              setFilterDateTo(e.target.value);
              if (e.target.value) { setFilterMonth(""); setFilterYear(""); }
            }}
            className="filter-select filter-date-input"
          />
          {(filterDateFrom || filterDateTo) && (
            <button
              className="date-range-clear"
              onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); }}
              title="Clear date range"
            >
              &times;
            </button>
          )}
        </div>

        <div className="filter-group">
          <select value={filterMonth} onChange={(e) => {
            setFilterMonth(e.target.value);
            if (e.target.value) { setFilterDateFrom(""); setFilterDateTo(""); }
          }} className="filter-select">
            <option value="">All Months</option>
            {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select value={filterYear} onChange={(e) => {
            setFilterYear(e.target.value);
            if (e.target.value) { setFilterDateFrom(""); setFilterDateTo(""); }
          }} className="filter-select">
            <option value="">All Years</option>
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
          </select>
        </div>

        <button className="export-btn" onClick={handleExportCSV}>
          <FaDownload /> Export CSV
        </button>

        <button className="export-btn" onClick={handleDownloadInvoices} disabled={downloading}>
          <FaDownload /> {downloading ? 'Downloading...' : 'Download Invoices'}
        </button>

        {role === "superadmin" && (
          <button className="deduction-btn" onClick={() => setShowDeductionModal(true)}>
            <FaPlus /> Add Deduction
          </button>
        )}
      </div>

      {role === "superadmin" && (
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-label">Total Transactions</div>
            <div className="stat-value">{(pagination.totalRecords || transactions.length) + filteredDeductions.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Transaction Amount</div>
            <div className="stat-value">Rs {totalAmount.toLocaleString()}</div>
          </div>
          <div className="stat-card deduction">
            <div className="stat-label">Total Deductions</div>
            <div className="stat-value">Rs {totalDeductionsAmt.toLocaleString()}</div>
          </div>
          <div className="stat-card net">
            <div className="stat-label">Net Amount</div>
            <div className="stat-value">Rs {netAmount.toLocaleString()}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Credits</div>
            <div className="stat-value">{transactions.filter((t) => t.type === "credit").length}</div>
          </div>
          <div className="stat-card pending">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{transactions.filter((t) => (t.status || "").toLowerCase() === "pending").length}</div>
          </div>
          <div className="stat-card failed">
            <div className="stat-label">Failed</div>
            <div className="stat-value">{transactions.filter((t) => (t.status || "").toLowerCase() === "failed").length}</div>
          </div>
        </div>
      )}

      <div className="transaction-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>Loading transactions...</div>
        ) : (
          <>
            <table className="transaction-table">
              <thead>
                <tr>
                  {visibleColumns.map((column) => (
                    <th key={column.key} className={getColumnClassName(column.key)} style={getColumnStyle(column.key)}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoiceRows.length > 0 ? (
                  invoiceRows.map((row) => {
                    const cells = [];
                    for (let i = 0; i < visibleColumns.length; i++) {
                      const col = visibleColumns[i];
                      if (col.key === "srNo") cells.push(<td key={col.key} className={getColumnClassName(col.key)} style={getColumnStyle(col.key)}>{row.srNo}</td>);
                      else if (col.key === "billDate") cells.push(<td key={col.key} className={getColumnClassName(col.key)} style={getColumnStyle(col.key)}>{row.billDate}</td>);
                      else if (col.key === "invoiceNo") cells.push(
                        <td key={col.key} className={getColumnClassName(col.key)} style={getColumnStyle(col.key)}>
                          {row.rawTxnId ? (
                            <button className="invoice-link-btn" onClick={() => handleViewInvoice(row.rawTxnId)} title="View Invoice">
                              <FaFileInvoice /> {row.invoiceNo}
                            </button>
                          ) : row.invoiceNo}
                        </td>
                      );
                      else if (col.key === "referenceNo") cells.push(<td key={col.key} className={getColumnClassName(col.key)} style={getColumnStyle(col.key)}>{row.referenceNo}</td>);
                      else if (col.key === "customerName") cells.push(<td key={col.key}>{row.customerName}</td>);
                      else if (col.key === "customerGstin") cells.push(<td key={col.key} className="nowrap-cell">{row.customerGstin}</td>);
                      else if (col.key === "customerPanNumber") cells.push(<td key={col.key} className="nowrap-cell">{row.customerPanNumber}</td>);
                      else if (col.key === "countryOfSupply") cells.push(<td key={col.key}>{row.countryOfSupply}</td>);
                      else if (col.key === "stateOfSupply") cells.push(<td key={col.key}>{row.stateOfSupply}</td>);
                      else if (col.key === "hsnCode") cells.push(<td key={col.key}>{row.hsnCode}</td>);
                      else if (col.key === "billingAddress") cells.push(<td key={col.key}>{row.billingAddress}</td>);
                      else if (col.key === "itemDescription") cells.push(
                        <td key={col.key}>
                          <div className="item-with-type">
                            <span className={`type-badge ${row.type === "debit" ? "debit" : "credit"}`}>
                              {row.type === "debit" ? "Debit" : "Credit"}
                            </span>
                            <span>{row.itemDescription}</span>
                          </div>
                        </td>
                      );
                      else if (col.key === "taxableAmount") cells.push(<td key={col.key} className="amount-cell">{formatAmount(row.taxableAmount)}</td>);
                      else if (col.key === "platformFee") cells.push(<td key={col.key} className="amount-cell">{formatAmount(row.platformFee)}</td>);
                      else if (col.key === "cgstAmount") cells.push(<td key={col.key} className="amount-cell">{formatAmount(row.cgstAmount)}</td>);
                      else if (col.key === "sgstAmount") cells.push(<td key={col.key} className="amount-cell">{formatAmount(row.sgstAmount)}</td>);
                      else if (col.key === "igstAmount") cells.push(<td key={col.key} className="amount-cell">{formatAmount(row.igstAmount)}</td>);
                      else if (col.key === "totalAmount") cells.push(<td key={col.key} className={`amount-cell total-amount-cell ${row.type === "debit" ? "debit-amount" : ""}`}>{formatAmount(row.totalAmount)}</td>);
                      else if (col.key === "companyName") cells.push(<td key={col.key}>{row.companyName}</td>);
                      else if (col.key === "gstinNumber") cells.push(<td key={col.key} className="nowrap-cell">{row.gstinNumber}</td>);
                      else if (col.key === "panNumber") cells.push(<td key={col.key} className="nowrap-cell">{row.panNumber}</td>);
                      else if (col.key === "companyAddress") cells.push(<td key={col.key}>{row.companyAddress}</td>);
                      else if (col.key === "updatedBy") cells.push(<td key={col.key}>{row.updatedBy}</td>);
                      else if (col.key === "paymentMode") cells.push(<td key={col.key}>{row.paymentMode}</td>);
                      else if (col.key === "utrNumber") cells.push(<td key={col.key}>{row.utrNumber}</td>);
                      else if (col.key === "attachmentUrl") cells.push(<td key={col.key}>{row.attachmentUrl}</td>);
                    }
                    return <tr key={row.rawTxnId}>{cells}</tr>;
                  })
                ) : (
                  <tr>
                    <td colSpan={visibleColumns.length} className="no-data">No entries found</td>
                  </tr>
                )}
              </tbody>
            </table>

            {pagination.totalPages > 0 && (
              <div className="pagination-bar">
                <div className="pagination-info">
                  Showing{" "}
                  <strong>{Math.min((pagination.page - 1) * pagination.limit + 1, pagination.totalRecords)}</strong>
                  {" – "}
                  <strong>{Math.min(pagination.page * pagination.limit, pagination.totalRecords)}</strong>
                  {" of "}
                  <strong>{pagination.totalRecords}</strong> records
                </div>

                <div className="pagination-controls">
                  {/* First */}
                  <button
                    className="page-btn"
                    disabled={pagination.page <= 1}
                    onClick={() => fetchTransactions(1)}
                    title="First page"
                  >
                    «
                  </button>
                  {/* Prev */}
                  <button
                    className="page-btn"
                    disabled={pagination.page <= 1}
                    onClick={() => fetchTransactions(pagination.page - 1)}
                    title="Previous page"
                  >
                    ‹
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const total = pagination.totalPages;
                    const cur = pagination.page;
                    const pages = [];
                    const add = (n) => pages.push(n);
                    const addEllipsis = (key) => pages.push("..." + key);

                    add(1);
                    if (cur > 4) addEllipsis("left");
                    for (let i = Math.max(2, cur - 2); i <= Math.min(total - 1, cur + 2); i++) add(i);
                    if (cur < total - 3) addEllipsis("right");
                    if (total > 1) add(total);

                    return pages.map((p) =>
                      typeof p === "string" ? (
                        <span key={p} className="page-ellipsis">…</span>
                      ) : (
                        <button
                          key={p}
                          className={`page-btn${p === cur ? " page-btn-active" : ""}`}
                          onClick={() => p !== cur && fetchTransactions(p)}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}

                  {/* Next */}
                  <button
                    className="page-btn"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => fetchTransactions(pagination.page + 1)}
                    title="Next page"
                  >
                    ›
                  </button>
                  {/* Last */}
                  <button
                    className="page-btn"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => fetchTransactions(pagination.totalPages)}
                    title="Last page"
                  >
                    »
                  </button>
                </div>

                <div className="pagination-size">
                  <label className="date-range-label">Rows per page</label>
                  <select
                    value={pagination.limit}
                    className="filter-select"
                    onChange={(e) => {
                      const newLimit = Number(e.target.value);
                      setPagination((p) => ({ ...p, limit: newLimit, page: 1 }));
                      setTimeout(() => fetchTransactions(1), 0);
                    }}
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showDeductionModal && (
        <div className="modal-overlay" onClick={() => setShowDeductionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Manual Deduction</h3>
              <button className="close-btn" onClick={() => setShowDeductionModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddDeduction} className="deduction-form">
              <div className="form-group">
                <label>Purpose</label>
                <input
                  type="text"
                  placeholder="Enter deduction purpose"
                  value={deductionForm.purpose}
                  onChange={(e) => setDeductionForm({ ...deductionForm, purpose: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Amount (Rs)</label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={deductionForm.amount}
                  onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label>Updated By</label>
                <input
                  type="text"
                  placeholder="Enter name"
                  value={deductionForm.updatedBy}
                  onChange={(e) => setDeductionForm({ ...deductionForm, updatedBy: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={deductionForm.date}
                  onChange={(e) => setDeductionForm({ ...deductionForm, date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>UTR Number</label>
                <input
                  type="text"
                  placeholder="Enter UTR number"
                  value={deductionForm.utrNumber}
                  onChange={(e) => setDeductionForm({ ...deductionForm, utrNumber: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Payment Mode</label>
                <select
                  value={deductionForm.paymentMode}
                  onChange={(e) => setDeductionForm({ ...deductionForm, paymentMode: e.target.value })}
                >
                  <option value="">Select Payment Mode</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
              <div className="form-group">
                <label>Attachment (Optional)</label>
                <input
                  type="text"
                  placeholder="Enter attachment URL or file reference"
                  value={deductionForm.attachmentUrl}
                  onChange={(e) => setDeductionForm({ ...deductionForm, attachmentUrl: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowDeductionModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={deductionSubmitting}>
                  {deductionSubmitting ? "Adding..." : "Add Deduction"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function buildDateFrom(month, year) {
  const y = year || new Date().getFullYear();
  const m = month ? String(month).padStart(2, "0") : "01";
  return y + "-" + m + "-01";
}

function buildDateTo(month, year) {
  const y = Number(year || new Date().getFullYear());
  const m = Number(month || 12);
  const lastDay = new Date(y, m, 0).getDate();
  return y + "-" + String(m).padStart(2, "0") + "-" + lastDay;
}
